import { cn } from '@mds/shared/lib/utils';

/**
 * A tab strip, drawn above what it switches between. A flagged tab says it
 * needs attention, so a problem on a tab that is not showing is not missed.
 */
export default function Tabs<K extends string>({
  label,
  tabs,
  active,
  onPick,
  flagged = () => false,
}: {
  label: string;
  tabs: readonly { key: K; label: string }[];
  active: K;
  onPick: (key: K) => void;
  flagged?: (key: K) => boolean;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="flex gap-1 border-b-2 border-hairline max-sm:[scrollbar-width:none] max-sm:overflow-x-auto sm:flex-wrap"
    >
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={active === t.key}
          onClick={() => onPick(t.key)}
          className={cn(
            'flex min-h-(--target-control) shrink-0 items-center gap-2 border-b-2 px-3 text-base whitespace-nowrap sm:-mb-0.5 sm:px-4 font-semibold transition-colors focus-visible:outline-(length:--focus-ring-width) focus-visible:-outline-offset-[3px] focus-visible:outline-(color:--focus-ring-color)',
            active === t.key
              ? 'border-brand-edge text-brand-text'
              : 'border-transparent text-ink-muted hover:text-brand-text',
          )}
        >
          {t.label}
          {flagged(t.key) && (
            <span className="rounded-full border border-danger-edge bg-danger-wash px-2 py-0.5 font-mono text-xs tracking-label text-danger-text uppercase">
              Needs attention
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
